import { useEffect, useState } from "react";
import axios from "axios";
import api from "../../lib/api";
import {
  PlusCircle, CheckCircle, Warning, Flag, Calendar, UserSwitch,
  ChatCircle, Paperclip, Lock, CircleNotch,
} from "@phosphor-icons/react";
import { API } from "../../App";

const ACTION_META = {
  "task.created": { icon: PlusCircle, color: "#10B981", label: "Task created" },
  "task.status_changed": { icon: CheckCircle, color: "#002FA7", label: "Status changed" },
  "task.priority_changed": { icon: Flag, color: "#F59E0B", label: "Priority changed" },
  "task.due_date_changed": { icon: Calendar, color: "#0E7490", label: "Due date changed" },
  "task.assigned_to_changed": { icon: UserSwitch, color: "#7C3AED", label: "Reassigned" },
  "task.assignee_status_changed": { icon: CircleNotch, color: "#0D9488", label: "Assignee status changed" },
  "task.force_closed": { icon: Lock, color: "#B45309", label: "Force-closed" },
  "task.comment_added": { icon: ChatCircle, color: "#475569", label: "Comment added" },
  "task.proof_uploaded": { icon: Paperclip, color: "#0EA5E9", label: "Proof uploaded" },
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true });
}

export default function TaskAuditTab({ taskId, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`${API}/crm/tasks/${taskId}/audit`, {
          params: {
            requester_user_id: user?.user_id || "",
            requester_name: user?.name || "",
            requester_role: user?.role || "",
          },
        });
        if (!cancelled) setRows(r.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 403 ? "No access to audit log." : "Could not load audit log.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [taskId, user?.user_id, user?.name, user?.role]);

  if (error) return <div data-testid="task-audit-error" className="text-sm text-red-600 border border-red-200 bg-red-50 p-3">{error}</div>;
  if (loading) return <div className="text-sm text-slate-400">Loading audit log…</div>;
  if (!rows.length) return <div data-testid="task-audit-empty" className="text-sm text-slate-400 italic">No audit events yet for this task.</div>;

  return (
    <div className="space-y-2" data-testid="task-audit-tab">
      {rows.map((e) => {
        const meta = ACTION_META[e.action] || { icon: CircleNotch, color: "#94A3B8", label: e.action };
        const Icon = meta.icon;
        const hasDiff = (e.old_value || e.new_value) && e.action !== "task.created" && e.action !== "task.comment_added" && e.action !== "task.proof_uploaded";
        return (
          <div key={e.log_id} data-testid={`task-audit-row-${e.log_id}`} className="border border-slate-200 p-3 flex gap-3 items-start bg-white">
            <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center" style={{ background: `${meta.color}15` }}>
              <Icon size={14} weight="bold" style={{ color: meta.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-bold text-slate-800">{meta.label}</div>
                <div className="text-[10px] text-slate-400 whitespace-nowrap">{fmtWhen(e.created_at)}</div>
              </div>
              {hasDiff && (
                <div className="text-xs text-slate-600 mt-1 font-mono">
                  <span className="line-through text-slate-400">{String(e.old_value || "—")}</span>
                  <span className="mx-1 text-slate-400">→</span>
                  <span className="text-slate-800">{String(e.new_value || "—")}</span>
                </div>
              )}
              {(e.action === "task.comment_added" || e.action === "task.proof_uploaded" || e.action === "task.force_closed") && e.new_value && (
                <div className="text-xs text-slate-600 mt-1 italic truncate" title={String(e.new_value)}>"{String(e.new_value)}"</div>
              )}
              <div className="text-[11px] text-slate-500 mt-1">
                by <span className="font-semibold">{e.user_name || "—"}</span>
                {e.user_role && <span className="text-slate-400"> · {String(e.user_role).toUpperCase()}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
